import { useState } from 'react'
import {
  Platform,
  Pressable,
  ScrollView,
  useWindowDimensions,
  View,
} from 'react-native'
import Animated, {
  interpolate,
  useAnimatedStyle,
} from 'react-native-reanimated'
import { useReanimatedKeyboardAnimation } from 'react-native-keyboard-controller'
import useRWD, { useMaxWindowDimensions } from '@ui/theme/useRWD'
import { intro } from '@ui/theme/values'
import Typography from '@ui/typography'
import cn from '@ui/utils/cn'

import ModalCard from '../ModalCard'
import useModalContext from '../useModalContext'
import { ModalCardComponentProps, ModalCardProps } from '../types'

const isWeb = Platform.OS === 'web'

function ModalWideCard({
  open,
  children,
  close,
  disableScroll,
  justifyContent = Platform.select({ default: 'flex-end', web: 'center' }),
  styleScrollView,
}: ModalCardComponentProps) {
  const { screen } = useRWD()
  const { height: keyboardHeight } = useReanimatedKeyboardAnimation()
  const { width } = useMaxWindowDimensions()
  const dimensions = useWindowDimensions()
  const { animation } = useModalContext()
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })
  const isContainerOverflowing =
    containerSize.height > screen.height - keyboardHeight.value * -1

  const padding = Platform.select({
    default: 32,
    web: 64,
  })

  const left = dimensions.width / 2 - width / 2

  const paddingTop = Platform.select({
    default: justifyContent === 'flex-start' ? intro.height + 2 * padding : 0,
    web: padding,
  })

  const styleModal = useAnimatedStyle(() => {
    return {
      transform: [
        {
          translateY:
            interpolate(animation.value, [0, 100], [400, 0]) +
            keyboardHeight.value,
        },
        {
          scale: interpolate(animation.value, [0, 100], [1.05, 1]),
        },
      ],
      opacity: interpolate(animation.value, [0, 100], [0, 1]),
      // pointerEvents: !isWeb && animation.value < 50 ? 'none' : 'auto',
      // height: isWeb ? screen.height - keyboardHeight.value * -1 : undefined,
      height: screen.height - keyboardHeight.value * -1,
    }
  })

  return (
    <Animated.View
      className={cn('absolute items-center', isWeb && 'pointer-events-none')}
      style={[
        {
          width,
          left,
          bottom: Platform.select({
            default: justifyContent === 'flex-end' ? -2 * padding : 0,
            web: 0,
          }),
          flex: 1,
        },
        styleModal,
      ]}
    >
      <ScrollView
        keyboardShouldPersistTaps="handled"
        className={cn(
          'relative inset-0 rounded-t-2xl',
          isWeb && 'pointer-events-none'
        )}
        scrollEnabled={!disableScroll}
        style={[
          {
            width,
            paddingTop,
            paddingBottom: Platform.select({
              default: justifyContent === 'flex-end' ? padding : 0,
              web: padding,
            }),
            flex: 1,
          },
          styleScrollView,
        ]}
        contentContainerStyle={{
          flexGrow: 1,
          justifyContent,
        }}
      >
        <Pressable
          className="absolute bottom-0 left-0 right-0 top-0"
          style={{ top: -paddingTop }}
          onPress={close}
        />
        <View
          className={cn(
            'flex flex-col overflow-hidden rounded-2xl bg-white',
            isWeb && open && 'pointer-events-auto'
          )}
          style={{
            // paddingBottom: Platform.select({
            //   default: justifyContent === 'center' ? 0 : 4 * padding,
            //   web: 0,
            // }),
            paddingBottom: Platform.select({
              default:
                justifyContent === 'flex-end'
                  ? isContainerOverflowing
                    ? 2 * padding
                    : padding
                  : paddingTop,
              web: justifyContent === 'flex-end' ? 0 : 0,
            }),
          }}
          onLayout={(event) => {
            const { width, height } = event.nativeEvent.layout
            setContainerSize({ width, height })
          }}
        >
          {children}
        </View>
      </ScrollView>
      {/* DEV DEBUG */}
      {/* <View className="pointer-events-none absolute bottom-0 left-0 right-0 top-0 flex flex-row gap-4">
        <Typography>
          {isContainerOverflowing ? 'OVERFLOW' : 'NO OVERFLOW'}
        </Typography>
        <Typography>CONTAINER: {containerSize.height}</Typography>
        <Typography>SCREEN: {screen.height}</Typography>
      </View> */}
    </Animated.View>
  )
}

export default function ModalWide(props: ModalCardProps) {
  return (
    <ModalCard {...props} CardComponent={ModalWideCard}>
      {props.children}
    </ModalCard>
  )
}
